import { Loader2, X } from "lucide-react";
import { BottomRightLaneSlot } from "@/components/layout/BottomRightLane";
import { FranklinSpeechBubble } from "@/components/chat/SidebarChat/FranklinSpeechBubble";
import {
  FRANKLIN_UPDATE_CLOSE_CLASS_NAME,
  FRANKLIN_UPDATE_CLOSE_GUTTER_CLASS_NAME,
  FRANKLIN_UPDATE_DESCRIPTION_CLASS_NAME,
  FRANKLIN_UPDATE_PRIMARY_ACTION_CLASS_NAME,
  FRANKLIN_UPDATE_TEXT_CLASS_NAME,
} from "@/components/chat/SidebarChat/franklinUpdateVisual";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { fill } from "@/lib/i18n/fill";
import { cn } from "@/lib/utils";
import type { Translations } from "@/lib/i18n/types";
import type { ProtocolSavePhase } from "./protocolSaveTypes";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];
type SaveCopy = ProtocolCopy["save"];

interface ProtocolSaveCardProps {
  phase: ProtocolSavePhase;
  filename: string;
  /** Why Save cannot run — no organisation or no user profile. Rendered
   *  instead of the button, so the card refuses rather than failing silently. */
  blockedReason?: string;
  copy: ProtocolCopy;
  onSave: () => void;
  onDismiss: () => void;
}

function headline(phase: ProtocolSavePhase, copy: SaveCopy): string {
  if (phase.phase === "uploading") return copy.uploading;
  if (phase.phase === "saved") return copy.saved;
  if (phase.phase === "unconfirmed") return copy.unconfirmed;
  if (phase.phase === "failed") return copy.failed;
  return copy.created;
}

function detail(phase: ProtocolSavePhase, filename: string, copy: SaveCopy): string {
  if (phase.phase === "unconfirmed") return copy.unconfirmedDetail;
  if (phase.phase === "saved") {
    // `folderPath` is null both for the project root and for a folder just
    // created whose path has not resolved — `inFolder` is what tells them
    // apart, so a root save never claims a folder it does not have.
    return phase.inFolder && phase.folderPath
      ? fill(copy.savedInFolder, { project: phase.projectName, folder: phase.folderPath })
      : fill(copy.savedInProject, { project: phase.projectName });
  }
  return fill(copy.createdDetail, { filename });
}

/**
 * The protocol, offered to the project it was generated for.
 *
 * Lives in the bottom-right lane rather than positioning itself: that corner is
 * shared with the upload card, the chat nudge and the toaster, and anything
 * that anchors itself there lands on top of one of them.
 */
export function ProtocolSaveCard({
  phase,
  filename,
  blockedReason,
  copy: protocolCopy,
  onSave,
  onDismiss,
}: ProtocolSaveCardProps) {
  // `retry` is shared with the issue step's failed state, so it lives one
  // level up rather than being duplicated into `save`.
  const copy = protocolCopy.save;
  const busy = phase.phase === "uploading";
  return (
    <BottomRightLaneSlot name="protocolSave">
      <FranklinSpeechBubble
        sizing="fit"
        role="status"
        aria-live="polite"
        data-testid="protocol-save-card"
      >
        <div className="flex items-start gap-2.5">
          {busy ? (
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-info" />
          ) : (
            <FranklinLogo size="xs" showBorder={false} className="shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                FRANKLIN_UPDATE_TEXT_CLASS_NAME,
                FRANKLIN_UPDATE_CLOSE_GUTTER_CLASS_NAME
              )}
            >
              {headline(phase, copy)}
            </p>
            <p className={cn(FRANKLIN_UPDATE_DESCRIPTION_CLASS_NAME, "mt-0.5 break-words")}>
              {detail(phase, filename, copy)}
            </p>
            {phase.phase !== "saved" && phase.phase !== "unconfirmed" && !busy && (
              <SaveAction
                failed={phase.phase === "failed"}
                blockedReason={blockedReason}
                retryLabel={protocolCopy.retry}
                saveLabel={copy.saveToProject}
                onSave={onSave}
              />
            )}
          </div>
        </div>
        <DismissButton label={copy.dismiss} onDismiss={onDismiss} />
      </FranklinSpeechBubble>
    </BottomRightLaneSlot>
  );
}

function DismissButton({ label, onDismiss }: { label: string; onDismiss: () => void }) {
  return (
    <button
      type="button"
      onClick={onDismiss}
      aria-label={label}
      className={FRANKLIN_UPDATE_CLOSE_CLASS_NAME}
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );
}

/** The offer to file the protocol — or, when Save cannot run, why not. */
function SaveAction(props: {
  failed: boolean;
  blockedReason?: string;
  retryLabel: string;
  saveLabel: string;
  onSave: () => void;
}) {
  return (
    <div className="mt-2">
      {props.blockedReason ? (
        <p className={FRANKLIN_UPDATE_DESCRIPTION_CLASS_NAME}>{props.blockedReason}</p>
      ) : (
        <button
          type="button"
          className={FRANKLIN_UPDATE_PRIMARY_ACTION_CLASS_NAME}
          onClick={props.onSave}
        >
          {props.failed ? props.retryLabel : props.saveLabel}
        </button>
      )}
    </div>
  );
}
